import { useRequiredContext } from "@redotech/react-util/context";
import CornerUpLeftSvg from "@redotech/redo-icons/corner-up-left_filled.svg";
import EditIcon from "@redotech/redo-icons/edit-02.svg";
import HideIcon from "@redotech/redo-icons/eye-off_filled.svg";
import ShowIcon from "@redotech/redo-icons/eye_filled.svg";
import ThumbsUp from "@redotech/redo-icons/thumbs-up.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import {
  CommentState,
  ConversationMessageType,
  ConversationPlatform,
  ExpandedConversation,
  ExpandedConversationMessage,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { toast } from "@redotech/redo-web/alert";
import { Menu, MenuItem } from "@redotech/redo-web/menu";
import { Text } from "@redotech/redo-web/text";
import "quill/dist/quill.snow.css";
import { memo, SetStateAction } from "react";
import { getConversation } from "../client/conversations";
import {
  deleteMetaComment,
  likeMetaComment,
  toggleFacebookComment,
  unlikeMetaComment,
} from "../client/facebook";
import { toggleInstagramComment } from "../client/instagram";
import * as styles from "../meta/meta-action-menu.module.css";

const tryAction = async (action: () => Promise<any>, message: string) => {
  try {
    const result = await action();
    if (result && result.success === false) {
      toast(message, { variant: "error" });
      return false;
    }
    return true;
  } catch (error) {
    console.error("Error in tryAction:", error);
    toast(message, { variant: "error" });
    return false;
  }
};

const getCommentId = (
  message: ExpandedConversationMessage,
  platform: ConversationPlatform,
): string => {
  const commentId =
    platform === ConversationPlatform.INSTAGRAM_COMMENTS
      ? message.instagram?.comment?.commentId
      : platform === ConversationPlatform.FACEBOOK_COMMENTS
        ? message.facebook?.comment?.commentId
        : null;

  if (!commentId) {
    throw new Error("Comment ID not found in message.");
  }

  return commentId;
};

export const MessageActionsMenu = memo(function MessageActionsMenu({
  conversation,
  actionMenuAnchor,
  message,
  setActionMenuAnchor,
  setPrivateReplyMessage,
  setPrivateReplyModalOpen,
  setIsActionLoading,
  setUpdateModalOpen,
  setActiveConversation,
}: {
  conversation: ExpandedConversation;
  actionMenuAnchor: HTMLElement | null;
  message: ExpandedConversationMessage;
  setActionMenuAnchor: React.Dispatch<SetStateAction<HTMLElement | null>>;
  setPrivateReplyMessage: (val: ExpandedConversationMessage) => void;
  setPrivateReplyModalOpen: (val: boolean) => void;
  setIsActionLoading: React.Dispatch<SetStateAction<boolean>>;
  setUpdateModalOpen: (val: boolean) => void;
  setActiveConversation: (conversation: MinimalConversation) => void;
}) {
  const cleanupMenu = () => {
    setActionMenuAnchor(null);
  };

  const replyWithDmHandler = () => {
    setPrivateReplyMessage(message);
    setPrivateReplyModalOpen(true);
    cleanupMenu();
  };

  const updateCommentHandler = () => {
    setUpdateModalOpen(true);
    cleanupMenu();
  };

  const toggleCommentHandler = async (hide: boolean) => {
    try {
      setIsActionLoading(true);
      const action = hide ? "hide" : "unhide";
      const commentId = getCommentId(message, conversation.platform);

      if (conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS) {
        await tryAction(
          () =>
            toggleInstagramComment(client, {
              conversationId: conversation._id,
              commentId,
              messageId: message._id,
              hide,
            }),
          `Failed to ${action} comment. Please try again.`,
        );

        if (message.instagram?.comment) {
          message.instagram.comment.state = hide
            ? CommentState.HIDDEN
            : CommentState.NORMAL;
        }
      } else if (
        conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS
      ) {
        await tryAction(
          () =>
            toggleFacebookComment(client, {
              conversationId: conversation._id,
              commentId,
              messageId: message._id,
              hide,
            }),
          `Failed to ${action} comment. Please try again.`,
        );

        if (message.facebook?.comment) {
          message.facebook.comment.state = hide
            ? CommentState.HIDDEN
            : CommentState.NORMAL;
        }
      }

      const updatedConversation = await getConversation(client, {
        conversationId: conversation._id,
      });
      setActiveConversation(updatedConversation);
    } catch (error) {
      const action = hide ? "hiding" : "unhiding";
      console.error(`Error ${action} comment:`, error);
      toast(
        `Failed to ${hide ? "hide" : "unhide"} comment. Please try again.`,
        { variant: "error" },
      );
    } finally {
      setIsActionLoading(false);
      cleanupMenu();
    }
  };
  const unlikeCommentHandler = async () => {
    try {
      setIsActionLoading(true);
      const commentId = getCommentId(message, conversation.platform);

      const success = await tryAction(
        () =>
          unlikeMetaComment(client, {
            conversationId: conversation._id,
            commentId,
            platform:
              conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS
                ? "instagram"
                : "facebook",
          }),
        "Failed to unlike comment. Please try again.",
      );

      if (success) {
        if (
          conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
          message.facebook?.comment
        ) {
          message.facebook.comment.state = CommentState.DELETED;
        } else if (
          conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS &&
          message.instagram?.comment
        ) {
          message.instagram.comment.state = CommentState.DELETED;
        }

        const updatedConversation = await getConversation(client, {
          conversationId: conversation._id,
        });
        setActiveConversation(updatedConversation);
      }
    } catch (error) {
      console.error("Error unliking comment:", error);
      toast("Failed to unlike comment. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsActionLoading(false);
      cleanupMenu();
    }
  };

  const likeCommentHandler = async () => {
    try {
      setIsActionLoading(true);
      const commentId = getCommentId(message, conversation.platform);

      const success = await tryAction(
        () =>
          likeMetaComment(client, {
            conversationId: conversation._id,
            commentId,
            platform:
              conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS
                ? "instagram"
                : "facebook",
          }),
        "Failed to like comment. Please try again.",
      );

      if (success) {
        if (
          conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
          message.facebook?.comment
        ) {
          message.facebook.comment.state = CommentState.DELETED;
        } else if (
          conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS &&
          message.instagram?.comment
        ) {
          message.instagram.comment.state = CommentState.DELETED;
        }

        const updatedConversation = await getConversation(client, {
          conversationId: conversation._id,
        });
        setActiveConversation(updatedConversation);
      }
    } catch (error) {
      console.error("Error liking comment:", error);
      toast("Failed to like comment. Please try again.", { variant: "error" });
    } finally {
      setIsActionLoading(false);
      cleanupMenu();
    }
  };

  const deleteCommentHandler = async () => {
    try {
      setIsActionLoading(true);
      const commentId = getCommentId(message, conversation.platform);

      const success = await tryAction(
        () =>
          deleteMetaComment(client, {
            conversationId: conversation._id,
            commentId,
            platform:
              conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS
                ? "instagram"
                : "facebook",
          }),
        "Failed to delete comment. Please try again.",
      );

      if (success) {
        if (
          conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
          message.facebook?.comment
        ) {
          message.facebook.comment.state = CommentState.DELETED;
        } else if (
          conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS &&
          message.instagram?.comment
        ) {
          message.instagram.comment.state = CommentState.DELETED;
        }

        const updatedConversation = await getConversation(client, {
          conversationId: conversation._id,
        });
        setActiveConversation(updatedConversation);
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      toast("Failed to delete comment. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsActionLoading(false);
      cleanupMenu();
    }
  };

  const client = useRequiredContext(RedoMerchantClientContext);
  const isCommentsPlatform = [
    ConversationPlatform.INSTAGRAM_COMMENTS,
    ConversationPlatform.FACEBOOK_COMMENTS,
  ].includes(conversation.platform);

  const hasComment =
    message.facebook?.comment?.commentId ||
    message.instagram?.comment?.commentId;

  const isCommentDeleted =
    (conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
      message.facebook?.comment?.deleted) ||
    message.facebook?.comment?.state === CommentState.DELETED ||
    (conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS &&
      message.instagram?.comment?.state === CommentState.DELETED);

  const canUpdateComment =
    message.type === ConversationMessageType.MERCHANT &&
    conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS;

  const isCommentHidden =
    (conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
      message.facebook?.comment?.state === CommentState.HIDDEN) ||
    (conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS &&
      message.instagram?.comment?.state === CommentState.HIDDEN);

  const hasCommentAlreadyBeenRepliedTo =
    (conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
      message.facebook?.comment?.hasBeenRepliedTo === true) ||
    (conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS &&
      message.instagram?.comment?.hasBeenRepliedTo === true);

  const canReply = useHasPermission(SupportPermissions.CREATE_REPLY);

  const hasCommentBeenLiked =
    (conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
      message.facebook?.comment?.liked) ||
    (conversation.platform === ConversationPlatform.INSTAGRAM_COMMENTS &&
      message.instagram?.comment?.liked);

  if (!isCommentsPlatform || isCommentDeleted || !hasComment) {
    return null;
  }

  return (
    <Menu
      anchor={actionMenuAnchor}
      onClose={() => {
        setActionMenuAnchor(null);
      }}
      open={!!actionMenuAnchor}
    >
      {!hasCommentAlreadyBeenRepliedTo && canReply && (
        <MenuItem onClick={() => replyWithDmHandler()}>
          <CornerUpLeftSvg className={styles.menuIcon} />
          <Text fontSize="xs">Reply privately</Text>
        </MenuItem>
      )}
      {message.type === ConversationMessageType.CUSTOMER &&
        (isCommentHidden ? (
          <MenuItem onClick={async () => await toggleCommentHandler(false)}>
            <ShowIcon className={styles.menuIcon} />
            <Text fontSize="xs">Unhide</Text>
          </MenuItem>
        ) : (
          <MenuItem onClick={async () => await toggleCommentHandler(true)}>
            <HideIcon className={styles.menuIcon} />
            <Text fontSize="xs">Hide</Text>
          </MenuItem>
        ))}
      {canUpdateComment && (
        <MenuItem onClick={async () => await updateCommentHandler()}>
          <EditIcon className={styles.menuIcon} />
          <Text fontSize="xs">Update</Text>
        </MenuItem>
      )}
      {!isCommentDeleted && (
        <MenuItem onClick={async () => await deleteCommentHandler()}>
          <TrashIcon className={styles.menuIcon} />
          <Text fontSize="xs">Delete</Text>
        </MenuItem>
      )}
      {conversation.platform === ConversationPlatform.FACEBOOK_COMMENTS &&
        (hasCommentBeenLiked ? (
          <MenuItem onClick={async () => await unlikeCommentHandler()}>
            <ThumbsUp className={styles.menuIcon} />
            <Text fontSize="xs">Unlike</Text>
          </MenuItem>
        ) : (
          <MenuItem onClick={async () => await likeCommentHandler()}>
            <ThumbsUp className={styles.menuIcon} />
            <Text fontSize="xs">Like</Text>
          </MenuItem>
        ))}
    </Menu>
  );
});
